import type { ReactNode } from "react";
import { ArrowRight, Terminal } from "lucide-react";

export function CodeBlock({
  language = "bash",
  children,
  caption,
}: {
  language?: string;
  children: string;
  caption?: string;
}) {
  return (
    <div className="border-4 border-black bg-[#0F172A] shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
      <div className="flex items-center justify-between border-b-4 border-black bg-black px-4 py-2">
        <div className="flex items-center gap-2">
          <Terminal className="h-3.5 w-3.5 text-[#A3E635]" strokeWidth={3} />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635]">
            {language}
          </span>
        </div>
        {caption ? (
          <span className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">
            {caption}
          </span>
        ) : null}
      </div>
      <pre className="overflow-x-auto p-4 text-[12.5px] leading-6 text-[#E2E8F0]">
        <code className="font-mono font-bold whitespace-pre">{children}</code>
      </pre>
    </div>
  );
}

export function Step({
  num,
  title,
  intent,
  children,
}: {
  num: string;
  title: string;
  intent?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-4 lg:grid-cols-[110px_1fr]">
      <div className="lg:pt-2">
        <div className="border-4 border-black bg-white px-3 py-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] inline-flex items-center gap-2">
          <span className="text-2xl font-black text-black leading-none">{num}</span>
        </div>
      </div>
      <div className="border-4 border-black bg-white/85 backdrop-blur-sm p-5 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
        <h3 className="text-xl font-black uppercase text-black sm:text-2xl">{title}</h3>
        {intent ? (
          <p className="mt-2 text-sm font-bold leading-6 text-zinc-700">{intent}</p>
        ) : null}
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </section>
  );
}

export function Note({
  variant = "tip",
  label,
  children,
}: {
  variant?: "tip" | "warn";
  label?: string;
  children: ReactNode;
}) {
  const bg = variant === "warn" ? "bg-[#FACC15]/70" : "bg-[#A3E635]/55";
  const tag = label ?? (variant === "warn" ? "注意" : "提示");
  return (
    <div className={`border-2 border-black ${bg} backdrop-blur-sm p-3 text-sm font-bold text-black`}>
      <span className="mr-2 inline-block border border-black bg-black px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.18em] text-white">
        {tag}
      </span>
      {children}
    </div>
  );
}

export function NextCta({
  href,
  label,
  tone = "lime",
}: {
  href: string;
  label: string;
  tone?: "lime" | "blue" | "white";
}) {
  const bg =
    tone === "blue"
      ? "bg-[#3B82F6] text-white"
      : tone === "white"
        ? "bg-white text-black"
        : "bg-[#A3E635] text-black";
  return (
    <a
      href={href}
      className={`group inline-flex items-center gap-2 border-2 border-black ${bg} px-4 py-2 text-sm font-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]`}
    >
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={3} />
    </a>
  );
}

export function QuickStartHero({
  role,
  roleColor,
  titleTop,
  titleBottom,
  lead,
  meta,
}: {
  role: string;
  roleColor: string;
  titleTop: string;
  titleBottom: string;
  lead: string;
  meta: string[];
}) {
  return (
    <section className="border-b-4 border-black bg-black px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div
          className="inline-flex items-center gap-2 border-4 border-white px-3 py-1.5"
          style={{ backgroundColor: roleColor }}
        >
          <span className="text-[11px] font-black uppercase tracking-[0.22em] text-black">
            {role}
          </span>
        </div>
        <h1 className="mt-6 text-4xl font-black uppercase leading-none text-white sm:text-6xl">
          {titleTop}
          <br />
          <span style={{ color: roleColor }}>{titleBottom}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base font-bold leading-7 text-zinc-300 sm:text-lg">
          {lead}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {meta.map((m) => (
            <span
              key={m}
              className="border-2 border-white/30 bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-white"
            >
              {m}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
